import React, { useState, useMemo, useEffect } from 'react';
import { useFinance } from '../context/FinanceContext';
import { 
  Plus, Trash2, Briefcase, CreditCard, TrendingUp, Clock, Edit2, X, ArrowUpRight, ArrowDownRight, Zap, Save, RefreshCcw, Calendar, History, Activity, Info, BarChart3,
  Sun, Moon, Lightbulb, CheckCircle2, ChevronRight, ChevronLeft, Coins, ShieldCheck, Sparkles, Scale, Layers, AlertTriangle, CalendarClock, Check, ShieldAlert, Wallet, FileText, CheckCircle
} from 'lucide-react';
import { JanuaryBaseline, BalanceSheetItem, CashFlowEvent, CreditCard as CreditCardItem, CreditCardPaymentRecord, CashLedgerEntry } from '../types';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ComposedChart, Line, Bar, Cell, Legend, ReferenceLine
} from 'recharts';

const P_DARK = {
  bg:     '#f8fafc',
  s1:     '#0D1625',
  s2:     '#111E33',
  s3:     '#162340',
  border: '#1A2940',
  muted:  '#475569',
  subtle: '#94a3b8',
  text:   '#F8FAFC',
  gold:   '#C9A84C',
  goldLt: '#E8C96A',
  teal:   '#2DD4BF',
  tealDk: '#0F8A7C',
  rose:   '#F87171',
  sky:    '#38BDF8',
  violet: '#A78BFA',
  amber:  '#FBBF24',
  green:  '#34D399',
  c1:'#3B82F6', c2:'#2DD4BF', c3:'#C9A84C', c4:'#A78BFA', c5:'#F87171', c6:'#34D399', c7:'#FB923C',
};

const P_LIGHT = {
  bg:     '#f8fafc',
  s1:     '#ffffff',
  s2:     '#ffffff',
  s3:     '#f1f5f9',
  border: '#e2e8f0',
  muted:  '#64748b',
  subtle: '#94a3b8',
  text:   '#0f172a',
  gold:   '#C9A84C',
  goldLt: '#E8C96A',
  teal:   '#0d9488',
  tealDk: '#0f766e',
  rose:   '#e11d48',
  sky:    '#0284c7',
  violet: '#7c3aed',
  amber:  '#d97706',
  green:  '#16a34a',
  c1:'#2563eb', c2:'#0d9488', c3:'#b45309', c4:'#7c3aed', c5:'#e11d48', c6:'#16a34a', c7:'#ea580c',
};

const getP = (isDarkMode: boolean) => isDarkMode ? P_DARK : P_LIGHT;
const fmt = (n: number, d = 0) => n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

const DarkTip = ({ active, payload, label }: any) => {
  const { isDarkMode, currency } = useFinance();
  const P = getP(isDarkMode);
  if (!active || !payload?.length) return null;
  const cardsList = payload[0]?.payload?.cardsList;
  return (
    <div style={{ background: P.s3, border: `1px solid ${P.border}`, borderRadius: 12, padding: '12px 16px', fontSize: 12, color: P.text, minWidth: 230, boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)' }}>
      {label && <div style={{ color: P.subtle, marginBottom: 8, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>}
      {payload.map((p: any, i: number) => {
        if (p.value === undefined || p.value === null) return null;
        const numVal = typeof p.value === 'number' ? p.value : parseFloat(p.value);
        const isNeg = !isNaN(numVal) && numVal < 0;
        const formatted = !isNaN(numVal)
          ? `${isNeg ? '−' : ''}${currency} ${fmt(Math.abs(numVal))}`
          : p.value;

        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: p.fill || p.color || p.stroke, display: 'inline-block' }} />
              <span style={{ color: P.subtle, fontSize: 11 }}>{p.name}:</span>
            </div>
            <span style={{ fontWeight: 700, fontFamily: "'Sora',sans-serif", fontSize: 12, color: isNeg ? P.rose : P.text }}>
              {formatted}
            </span>
          </div>
        );
      })}
      {payload[0]?.payload?.AvailableCashBalance !== undefined && (
        <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${P.border}`, fontSize: 11, color: P.teal }}>
          <span style={{ fontWeight: 700 }}>Month Ledger Cash: </span>
          <span>{currency} {fmt(payload[0]?.payload?.AvailableCashBalance)}</span>
        </div>
      )}
      {cardsList && (
        <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${P.border}`, fontSize: 10, color: P.rose }}>
          <span style={{ fontWeight: 700 }}>Due Cards: </span>{cardsList}
        </div>
      )}
    </div>
  );
};

const BalanceSheet: React.FC = () => {
  const { 
    balanceSheet, addBalanceSheetItem, updateBalanceSheetItem, deleteBalanceSheetItem, 
    investments, investmentCategories, cashFlowEvents, addCashFlowEvent, updateCashFlowEvent, deleteCashFlowEvent, 
    currency, convertToDisplay, convertFromInput, janBaseline, setJanBaseline,
    isDarkMode, setIsDarkMode,
    passiveIncomeStats,
    creditCards, addCreditCard, updateCreditCard, deleteCreditCard, toggleCreditCardPaid,
    creditCardLedger, recordCreditCardPayment, deleteCreditCardPaymentRecord,
    availableCash, setAvailableCash,
    cashLedgerHistory, syncCashLedger, updateCashLedgerEntry, deleteCashLedgerEntry, getLastHistoricalCashForMonth,
    selectedYear, setSelectedYear, availableYears
  } = useFinance();

  const P = getP(isDarkMode);
  
  // Active Cash for the selected year (derived from cashLedgerHistory for selectedYear, or availableCash for current year)
  const activeCashForYear = useMemo(() => {
    const entriesForYear = cashLedgerHistory.filter(e => e.year === selectedYear);
    if (entriesForYear.length > 0) {
      return entriesForYear[0].amount;
    }
    if (selectedYear === new Date().getFullYear()) {
      return availableCash;
    }
    return 0;
  }, [cashLedgerHistory, selectedYear, availableCash]);

  // Available Cash Input state for real-time rule calculation
  const [cashInput, setCashInput] = useState<string>(() => convertToDisplay(activeCashForYear).toString());

  useEffect(() => {
    setCashInput(activeCashForYear > 0 ? convertToDisplay(activeCashForYear).toString() : '0');
  }, [selectedYear, activeCashForYear, currency]);

  const handleCashChange = (val: string) => {
    setCashInput(val);
    const parsed = parseFloat(val);
    if (!isNaN(parsed) && parsed >= 0) {
      setAvailableCash(convertFromInput(parsed));
    } else if (val === '') {
      setAvailableCash(0);
    }
  };

  // Historical Cash Entry Editing State
  const [editingCashEntryId, setEditingCashEntryId] = useState<string | null>(null);
  const [editCashAmount, setEditCashAmount] = useState<string>('');
  const [editCashNote, setEditCashNote] = useState<string>('');

  const handleStartEditCashEntry = (entry: CashLedgerEntry) => {
    setEditingCashEntryId(entry.id);
    setEditCashAmount(convertToDisplay(entry.amount).toString());
    setEditCashNote(entry.note || '');
  };

  const handleCancelEditCashEntry = () => {
    setEditingCashEntryId(null);
    setEditCashAmount('');
    setEditCashNote('');
  };

  const handleSaveCashEntry = (id: string) => {
    const parsed = parseFloat(editCashAmount);
    if (!isNaN(parsed) && parsed >= 0) {
      updateCashLedgerEntry(id, convertFromInput(parsed), editCashNote);
      setEditingCashEntryId(null);
    }
  };

  // Sync Ledger Feedback State
  const [syncFeedback, setSyncFeedback] = useState<{
    timestamp: string;
    amount: number;
    previous?: number;
    monthName: string;
  } | null>(null);

  // Sync Ledger Cash Handler:
  // Registers timestamp of when cash ledger is updated, updates account ledger cash amount of current,
  // and keeps track of the previous on the history of the ledger for the selected year.
  const handleSyncLedgerCash = () => {
    const rawVal = parseFloat(cashInput);
    const targetAmt = !isNaN(rawVal) && rawVal >= 0 ? convertFromInput(rawVal) : activeCashForYear;
    const { entry, previousAmount } = syncCashLedger(targetAmt, undefined, selectedYear);
    setSyncFeedback({
      timestamp: entry.timestampFormatted,
      amount: targetAmt,
      previous: previousAmount,
      monthName: entry.monthName
    });
    setTimeout(() => {
      setSyncFeedback(null);
    }, 7000);
  };

  // Plan/Record Cash for Specific Month & Year State
  const [isPlanCashModalOpen, setIsPlanCashModalOpen] = useState(false);
  const [planCashForm, setPlanCashForm] = useState({
    monthIndex: 0,
    year: selectedYear,
    amount: '',
    note: ''
  });

  useEffect(() => {
    setPlanCashForm(prev => ({ ...prev, year: selectedYear }));
  }, [selectedYear]);

  const handlePlanCashSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseFloat(planCashForm.amount);
    if (isNaN(parsed) || parsed < 0) return;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    syncCashLedger(
      convertFromInput(parsed),
      planCashForm.monthIndex,
      planCashForm.year,
      planCashForm.note.trim() || `Planned cash record for ${months[planCashForm.monthIndex]} ${planCashForm.year}`
    );
    setIsPlanCashModalOpen(false);
    setPlanCashForm({ monthIndex: 0, year: selectedYear, amount: '', note: '' });
  };

  // Only display cash history for the selected year (hiding previous years' records)
  const displayedCashHistory = useMemo(() => {
    return cashLedgerHistory.filter(e => e.year === selectedYear);
  }, [cashLedgerHistory, selectedYear]);

  // Only display credit card settlements for the selected year (hiding previous years' records)
  const displayedCreditCardLedger = useMemo(() => {
    return creditCardLedger.filter(e => e.year === selectedYear);
  }, [creditCardLedger, selectedYear]);

  // Ledger View Tab: 'cashLedger' | 'creditCardLedger' | 'accountLedger'
  const [activeLedgerTab, setActiveLedgerTab] = useState<'cashLedger' | 'creditCardLedger' | 'accountLedger'>('cashLedger');
  const [isHistoricalLedgerOpen, setIsHistoricalLedgerOpen] = useState(true);

  // Default dates anchored to the selected year
  const defaultDateForYear = useMemo(() => {
    const now = new Date();
    return selectedYear === now.getFullYear()
      ? now.toISOString().split('T')[0]
      : `${selectedYear}-01-01`;
  }, [selectedYear]);

  const defaultCardDeadLine = useMemo(() => {
    const now = new Date();
    const targetMonth = selectedYear === now.getFullYear() ? now.getMonth() : 0;
    const targetDay = selectedYear === now.getFullYear() ? Math.min(now.getDate() + 18, 28) : 15;
    return `${selectedYear}-${String(targetMonth + 1).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`;
  }, [selectedYear]);

  // Forms State
  const [bsForm, setBsForm] = useState({ 
    id: '', 
    name: '', 
    type: 'Liability' as 'Asset' | 'Liability', 
    value: '', 
    startDate: defaultDateForYear,
    endDate: ''
  });
  
  const [cfForm, setCfForm] = useState({ 
    id: '', 
    desc: '', 
    amt: '', 
    type: 'Inflow' as 'Inflow' | 'Outflow', 
    startDate: defaultDateForYear,
    endDate: ''
  });

  useEffect(() => {
    if (!bsForm.id) {
      setBsForm(prev => ({ ...prev, startDate: defaultDateForYear }));
    }
    if (!cfForm.id) {
      setCfForm(prev => ({ ...prev, startDate: defaultDateForYear }));
    }
  }, [defaultDateForYear]);
  
  const [isBaselineModalOpen, setIsBaselineModalOpen] = useState(false);
  const [baselineInput, setBaselineInput] = useState({ netWorth: '', assets: '', liabilities: '', avgInflow: '', avgOutflow: '' });

  // Credit Cards Current Liabilities State
  const [isCcModalOpen, setIsCcModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<CreditCardItem | null>(null);
  const [cardToDelete, setCardToDelete] = useState<CreditCardItem | null>(null);
  const [ccFilter, setCcFilter] = useState<'all' | 'unpaid' | 'paid'>('all');
  const [ccForm, setCcForm] = useState({
    name: '',
    issuer: 'American Express',
    last4: '',
    amountToPay: '',
    minimumPayment: '',
    deadline: defaultCardDeadLine,
    creditLimit: '',
    apr: '',
    notes: ''
  });

  const openAddCardModal = () => {
    setEditingCard(null);
    setCcForm({
      name: '',
      issuer: 'American Express',
      last4: '',
      amountToPay: '',
      minimumPayment: '',
      deadline: defaultCardDeadLine,
      creditLimit: '',
      apr: '',
      notes: ''
    });
    setIsCcModalOpen(true);
  };

  const openEditCardModal = (card: CreditCardItem) => {
    setEditingCard(card);
    setCcForm({
      name: card.name,
      issuer: card.issuer || 'American Express',
      last4: card.last4 || '',
      amountToPay: convertToDisplay(card.amountToPay).toString(),
      minimumPayment: card.minimumPayment !== undefined ? convertToDisplay(card.minimumPayment).toString() : '',
      deadline: card.deadline,
      creditLimit: card.creditLimit !== undefined ? convertToDisplay(card.creditLimit).toString() : '',
      apr: card.apr !== undefined ? card.apr.toString() : '',
      notes: card.notes || ''
    });
    setIsCcModalOpen(true);
  };

  const handleCcSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ccForm.name.trim() || !ccForm.amountToPay || !ccForm.deadline) return;

    const parsedAmt = convertFromInput(parseFloat(ccForm.amountToPay) || 0);
    const parsedMin = ccForm.minimumPayment ? convertFromInput(parseFloat(ccForm.minimumPayment) || 0) : undefined;
    const parsedLimit = ccForm.creditLimit ? convertFromInput(parseFloat(ccForm.creditLimit) || 0) : undefined;
    const parsedApr = ccForm.apr ? parseFloat(ccForm.apr) : undefined;

    if (editingCard) {
      updateCreditCard({
        ...editingCard,
        name: ccForm.name.trim(),
        issuer: ccForm.issuer.trim(),
        last4: ccForm.last4.replace(/\D/g, '').slice(-4),
        amountToPay: parsedAmt,
        minimumPayment: parsedMin,
        deadline: ccForm.deadline,
        creditLimit: parsedLimit,
        apr: parsedApr,
        notes: ccForm.notes.trim()
      });
    } else {
      addCreditCard({
        name: ccForm.name.trim(),
        issuer: ccForm.issuer.trim(),
        last4: ccForm.last4.replace(/\D/g, '').slice(-4),
        amountToPay: parsedAmt,
        minimumPayment: parsedMin,
        deadline: ccForm.deadline,
        creditLimit: parsedLimit,
        apr: parsedApr,
        isPaid: false,
        notes: ccForm.notes.trim()
      });
    }

    setIsCcModalOpen(false);
    setEditingCard(null);
  };

  // Credit Cards belonging to the selected year (hiding cards from previous years)
  const cardsForYear = useMemo(() => {
    const yearStr = selectedYear.toString();
    return creditCards.filter(c => {
      if (!c.deadline) return true;
      return c.deadline.startsWith(yearStr);
    });
  }, [creditCards, selectedYear]);

  // Credit Card Metrics for Selected Year
  const creditCardMetrics = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    let totalDebt = 0;
    let totalMinDue = 0;
    let totalLimit = 0;
    const unpaidCards: CreditCardItem[] = [];

    cardsForYear.forEach(c => {
      totalLimit += c.creditLimit || 0;
      if (!c.isPaid) {
        totalDebt += c.amountToPay;
        totalMinDue += c.minimumPayment || 0;
        unpaidCards.push(c);
      }
    });

    const sortedUnpaid = [...unpaidCards].sort((a, b) => a.deadline.localeCompare(b.deadline));
    const nextCard = sortedUnpaid[0] || null;

    let daysUntilNext: number | null = null;
    if (nextCard) {
      const diffTime = new Date(nextCard.deadline).getTime() - new Date(today).getTime();
      daysUntilNext = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }

    const utilizationRate = totalLimit > 0 ? (totalDebt / totalLimit) * 100 : 0;

    return {
      totalDebt,
      totalMinDue,
      totalLimit,
      unpaidCount: unpaidCards.length,
      totalCount: cardsForYear.length,
      nextCard,
      daysUntilNext,
      utilizationRate
    };
  }, [cardsForYear]);

  const getDeadlineStatus = (deadlineStr: string, isPaid?: boolean) => {
    if (isPaid) {
      return { label: 'Paid & Settled', tone: 'paid', days: 0 };
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(deadlineStr);
    dueDate.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { label: `Overdue by ${Math.abs(diffDays)}d`, tone: 'overdue', days: diffDays };
    }
    if (diffDays === 0) {
      return { label: 'Due Today', tone: 'urgent', days: 0 };
    }
    if (diffDays <= 5) {
      return { label: `Due in ${diffDays}d`, tone: 'soon', days: diffDays };
    }
    return { label: `Due in ${diffDays}d`, tone: 'upcoming', days: diffDays };
  };

  const filteredCards = useMemo(() => {
    if (ccFilter === 'unpaid') return cardsForYear.filter(c => !c.isPaid);
    if (ccFilter === 'paid') return cardsForYear.filter(c => c.isPaid);
    return cardsForYear;
  }, [cardsForYear, ccFilter]);

  // Balance sheet items belonging to the selected year (hiding items from previous years)
  const displayedBalanceSheet = useMemo(() => {
    const yearStart = `${selectedYear}-01-01`;
    const yearEnd = `${selectedYear}-12-31`;
    return balanceSheet.filter(item => {
      if (item.endDate && item.endDate < yearStart) return false;
      if (item.startDate && item.startDate > yearEnd) return false;
      if (item.startDate && item.startDate < yearStart) return false;
      return true;
    });
  }, [balanceSheet, selectedYear]);

  // Cash flow forecast rules belonging to the selected year (hiding rules from previous years)
  const displayedCashFlowEvents = useMemo(() => {
    const yearStart = `${selectedYear}-01-01`;
    const yearEnd = `${selectedYear}-12-31`;
    return cashFlowEvents.filter(ev => {
      if (ev.endDate && ev.endDate < yearStart) return false;
      if (ev.startDate && ev.startDate > yearEnd) return false;
      if (ev.startDate && ev.startDate < yearStart) return false;
      return true;
    });
  }, [cashFlowEvents, selectedYear]);

  // Current State Summary (Top Metrics)
  const currentSnapshot = useMemo(() => {
    const today = new Date();
    const currentYear = selectedYear;
    // Keep investment portfolio categories active (liquidated only when closed)
    const invTotal = investmentCategories.reduce((acc, curr) => acc + curr.currentValue, 0);
    
    // 1. Assets and Liabilities based on today, hiding rules from prior years
    const activeLedger = balanceSheet.filter(item => {
      if (item.startDate && item.startDate < `${currentYear}-01-01`) return false;
      const start = new Date(item.startDate);
      const end = item.endDate ? new Date(item.endDate) : new Date('2099-12-31');
      const targetDate = selectedYear === today.getFullYear()
        ? today
        : new Date(selectedYear, 11, 31, 23, 59, 59);
      return targetDate >= start && targetDate <= end;
    });

    const manualAssets = activeLedger.filter(i => i.type === 'Asset').reduce((acc, curr) => acc + curr.value, 0);
    const liabilities = activeLedger.filter(i => i.type === 'Liability').reduce((acc, curr) => acc + curr.value, 0);
    const totalAssets = invTotal + manualAssets;
    const netWorth = totalAssets - liabilities;

    // 2. Average Inflow and Outflow (Sum of year / 12)
    let totalYearlyInflow = 0;
    let totalYearlyOutflow = 0;
    
    for (let i = 0; i < 12; i++) {
      const monthStart = new Date(currentYear, i, 1);
      const monthEnd = new Date(currentYear, i + 1, 0);
      
      const flowsInMonth = cashFlowEvents.filter(event => {
        if (event.startDate && event.startDate < `${currentYear}-01-01`) return false;
        const start = new Date(event.startDate);
        const end = event.endDate ? new Date(event.endDate) : new Date('2099-12-31');
        return start <= monthEnd && end >= monthStart;
      });
      
      totalYearlyInflow += flowsInMonth.filter(f => f.type === 'Inflow').reduce((acc, curr) => acc + curr.amount, 0);
      totalYearlyOutflow += flowsInMonth.filter(f => f.type === 'Outflow').reduce((acc, curr) => acc + curr.amount, 0);
    }
    
    const avgInflow = totalYearlyInflow / 12;
    const avgOutflow = totalYearlyOutflow / 12;

    const solvencyRatio = totalAssets > 0 ? (netWorth / totalAssets) * 100 : 0;
    const debtRatio = totalAssets > 0 ? (liabilities / totalAssets) * 100 : 0;
    const cashRunway = avgOutflow > 0 ? manualAssets / avgOutflow : 12;
    const passiveCoverage = avgOutflow > 0 ? (passiveIncomeStats.totalMonthlyPassive / avgOutflow) * 100 : 0;
    const reinvestmentRate = avgInflow > 0 ? Math.max(((avgInflow - avgOutflow) / avgInflow) * 100, 0) : 0;
    const assetCoverage = liabilities > 0 ? totalAssets / liabilities : 10;

    return { 
      netWorth, totalAssets, liabilities, manualAssets, invTotal,
      avgInflow, avgOutflow,
      solvencyRatio, debtRatio, cashRunway, passiveCoverage, reinvestmentRate, assetCoverage
    };
  }, [investmentCategories, balanceSheet, cashFlowEvents, passiveIncomeStats, selectedYear]);

  // Utility to calculate % change
  const calcChange = (current: number, baseline: number | undefined) => {
    if (baseline === undefined || baseline === null || baseline === 0) return null;
    const diff = ((current - baseline) / Math.abs(baseline)) * 100;
    return diff;
  };

  // KPI Definition
  const kpiData = useMemo(() => [
    { label: 'Net Worth', value: currentSnapshot.netWorth, baseline: janBaseline?.netWorth },
    { label: 'Total Assets', value: currentSnapshot.totalAssets, baseline: janBaseline?.assets },
    { label: 'Total Liabilities', value: currentSnapshot.liabilities, baseline: janBaseline?.liabilities },
    { label: 'Avg Inflow', value: currentSnapshot.avgInflow, baseline: janBaseline?.avgInflow },
    { label: 'Avg Outflow', value: currentSnapshot.avgOutflow, baseline: janBaseline?.avgOutflow }
  ], [currentSnapshot, janBaseline]);

  // Trajectory Aggregator for Graphs (Takes into account credit cards due in deadline month and anchors balance from activeCashForYear)
  const trajectoryData = useMemo(() => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const today = new Date();
    const currentSystemYear = today.getFullYear();
    const currentYear = selectedYear;
    const currentMonthIndex = (selectedYear === currentSystemYear) ? today.getMonth() : 0;
    const invVal = investmentCategories.reduce((acc, curr) => acc + curr.currentValue, 0);

    // Calculate monthly flows and credit card obligations for each month
    const monthlyStats = months.map((m, i) => {
      const monthStart = new Date(currentYear, i, 1);
      const monthEnd = new Date(currentYear, i + 1, 0, 23, 59, 59, 999);

      const activeLedger = balanceSheet.filter(item => {
        if (item.startDate && item.startDate < `${currentYear}-01-01`) return false;
        const start = new Date(item.startDate);
        const end = item.endDate ? new Date(item.endDate) : new Date('2099-12-31');
        return start <= monthEnd && end >= monthStart;
      });

      const ledgerAssets = activeLedger.filter(it => it.type === 'Asset').reduce((a, c) => a + c.value, 0);
      const ledgerLiabilities = activeLedger.filter(it => it.type === 'Liability').reduce((a, c) => a + c.value, 0);
      const totalAssets = invVal + ledgerAssets;
      const netWorth = totalAssets - ledgerLiabilities;

      const activeFlows = cashFlowEvents.filter(event => {
        if (event.startDate && event.startDate < `${currentYear}-01-01`) return false;
        const start = new Date(event.startDate);
        const end = event.endDate ? new Date(event.endDate) : new Date('2099-12-31');
        return start <= monthEnd && end >= monthStart;
      });

      const rawInflow = activeFlows.filter(f => f.type === 'Inflow').reduce((a, c) => a + c.amount, 0);
      const rawForecastOutflow = activeFlows.filter(f => f.type === 'Outflow').reduce((a, c) => a + c.amount, 0);

      // Inflow from forecast rules (fallback to snapshot average or baseline if no active events)
      const inflow = rawInflow > 0 ? rawInflow : (currentSnapshot.avgInflow > 0 ? currentSnapshot.avgInflow : 24000);
      // Outflow from forecast rules (fallback to snapshot average or baseline if no active events)
      const forecastOutflow = rawForecastOutflow > 0 ? rawForecastOutflow : (currentSnapshot.avgOutflow > 0 ? currentSnapshot.avgOutflow : 12000);

      // Find credit cards whose deadline falls in this specific month for the selected year
      const cardsDueInMonth = cardsForYear.filter(card => {
        if (!card.deadline) return false;
        const parts = card.deadline.split('-');
        if (parts.length >= 2) {
          const y = parseInt(parts[0], 10);
          const mNum = parseInt(parts[1], 10);
          return y === currentYear && mNum === (i + 1);
        }
        const d = new Date(card.deadline);
        if (!isNaN(d.getTime())) {
          return d.getFullYear() === currentYear && d.getMonth() === i;
        }
        return false;
      });

      // For current month in current year, include any unpaid overdue cards from prior months in this year
      const overdueUnpaidCards = (selectedYear === currentSystemYear && i === currentMonthIndex)
        ? cardsForYear.filter(card => {
            if (card.isPaid || !card.deadline) return false;
            const parts = card.deadline.split('-');
            if (parts.length >= 2) {
              const y = parseInt(parts[0], 10);
              const mNum = parseInt(parts[1], 10);
              return y === currentYear && mNum < (currentMonthIndex + 1);
            }
            const d = new Date(card.deadline);
            if (!isNaN(d.getTime())) {
              return d.getFullYear() === currentYear && d.getMonth() < currentMonthIndex;
            }
            return false;
          })
        : [];

      const allCardsForMonth = [...cardsDueInMonth, ...overdueUnpaidCards];

      // Amount of credit cards due in this month (considered as outflow regardless of paid status)
      const creditCardAmountInMonth = allCardsForMonth.reduce((sum, c) => sum + c.amountToPay, 0);
      const totalCreditCardsInMonth = creditCardAmountInMonth;

      // Total Outflow includes forecast rules registered + credit card amounts due in this month (regardless of status)
      const totalOutflow = forecastOutflow + creditCardAmountInMonth;

      // Net monthly cash flow
      const netMonthlyFlow = inflow - totalOutflow;

      return {
        monthName: m,
        monthIndex: i,
        totalAssets,
        ledgerLiabilities,
        netWorth,
        inflow,
        forecastOutflow,
        creditCardAmountInMonth,
        totalCreditCardsInMonth,
        totalOutflow,
        netMonthlyFlow,
        cardsCount: allCardsForMonth.length,
        cardsList: allCardsForMonth.length > 0 
          ? allCardsForMonth.map(c => `${c.name}: −${currency} ${convertToDisplay(c.amountToPay).toLocaleString()}${c.isPaid ? ' (Settled)' : ''}`).join(', ')
          : undefined
      };
    });

    // 1. Cash Balance Forecast Trend Line for selected year:
    const janRegistered = getLastHistoricalCashForMonth(0, currentYear);
    const baseYearCash = janRegistered !== null ? janRegistered : (activeCashForYear > 0 ? activeCashForYear : 0);

    const forecastBalances: number[] = new Array(12).fill(0);
    for (let i = 0; i < 12; i++) {
      const prevCash = i === 0 ? baseYearCash : forecastBalances[i - 1];
      // Outflow includes forecast rule + credit card obligations regardless of paid status
      forecastBalances[i] = prevCash + monthlyStats[i].inflow - monthlyStats[i].totalOutflow;
    }

    // 2. Adjusted Cash Balance:
    const balances: number[] = new Array(12).fill(0);
    const monthlyLedgerCash: number[] = new Array(12).fill(0);

    for (let i = 0; i < 12; i++) {
      let prevMonthRegisteredCash: number;
      if (i === 0) {
        prevMonthRegisteredCash = janRegistered !== null ? janRegistered : baseYearCash;
      } else {
        const prevHistorical = getLastHistoricalCashForMonth(i - 1, currentYear);
        prevMonthRegisteredCash = prevHistorical !== null ? prevHistorical : balances[i - 1];
      }

      monthlyLedgerCash[i] = prevMonthRegisteredCash;

      // credit card balance + outflow rule (credit card considered as outflow regardless of status)
      const creditCardBalance = monthlyStats[i].creditCardAmountInMonth;
      const outflowRule = monthlyStats[i].forecastOutflow;
      balances[i] = prevMonthRegisteredCash + monthlyStats[i].inflow - (creditCardBalance + outflowRule);
    }

    return monthlyStats.map((st, i) => ({
      name: st.monthName,
      monthIndex: i,
      Assets: convertToDisplay(st.totalAssets),
      Liabilities: convertToDisplay(st.ledgerLiabilities),
      'Net Worth': convertToDisplay(st.netWorth),
      Inflow: convertToDisplay(st.inflow),
      ForecastOutflow: convertToDisplay(st.forecastOutflow),
      ForecastOutflowNegative: -convertToDisplay(st.forecastOutflow),
      CreditCards: convertToDisplay(st.creditCardAmountInMonth),
      CreditCardsNegative: -convertToDisplay(st.creditCardAmountInMonth),
      TotalCreditCardsScheduled: convertToDisplay(st.totalCreditCardsInMonth),
      Outflow: convertToDisplay(st.totalOutflow),
      TotalOutflowNegative: -convertToDisplay(st.totalOutflow),
      NetCashFlow: convertToDisplay(st.netMonthlyFlow),
      Balance: convertToDisplay(balances[i]),
      CashBalanceForecast: convertToDisplay(forecastBalances[i]),
      AvailableCashBalance: convertToDisplay(monthlyLedgerCash[i]),
      HistoricalCashConsidered: convertToDisplay(monthlyLedgerCash[i]),
      cardsCount: st.cardsCount,
      cardsList: st.cardsList,
      isCurrentMonth: (selectedYear === currentSystemYear) && (i === today.getMonth())
    }));
  }, [balanceSheet, investmentCategories, cashFlowEvents, cardsForYear, activeCashForYear, cashLedgerHistory, getLastHistoricalCashForMonth, convertToDisplay, currency, selectedYear]);

  // Current Month Active Forecast Outflows
  const currentMonthForecastOutflow = useMemo(() => {
    const today = new Date();
    const currentYear = selectedYear;
    const currentMonth = (selectedYear === today.getFullYear()) ? today.getMonth() : 0;
    const monthStart = new Date(currentYear, currentMonth, 1);
    const monthEnd = new Date(currentYear, currentMonth + 1, 0, 23, 59, 59);

    const activeFlows = cashFlowEvents.filter(event => {
      if (event.startDate && event.startDate < `${currentYear}-01-01`) return false;
      const start = new Date(event.startDate);
      const end = event.endDate ? new Date(event.endDate) : new Date('2099-12-31');
      return start <= monthEnd && end >= monthStart;
    });

    const outflowEvents = activeFlows.filter(f => f.type === 'Outflow');
    const inflowEvents = activeFlows.filter(f => f.type === 'Inflow');
    const outflow = outflowEvents.reduce((a, c) => a + c.amount, 0);
    const inflow = inflowEvents.reduce((a, c) => a + c.amount, 0);

    return {
      monthOutflow: outflow > 0 ? outflow : currentSnapshot.avgOutflow,
      monthInflow: inflow > 0 ? inflow : currentSnapshot.avgInflow,
      activeCount: outflowEvents.length,
      activeInflowCount: inflowEvents.length
    };
  }, [cashFlowEvents, currentSnapshot, selectedYear]);

  // Single Left Y-Axis dynamic domain & ticks covering inflows, negative outflows, and cash balance trend
  // Explicitly includes 0 so the zero reference baseline is always aligned with tick 0
  const yAxisConfig = useMemo(() => {
    if (!trajectoryData.length) return { domain: [0, 10000], ticks: [0, 5000, 10000] };

    let minVal = 0;
    let maxVal = 0;

    trajectoryData.forEach(d => {
      if (d.TotalOutflowNegative < minVal) minVal = d.TotalOutflowNegative;
      if (d.Balance < minVal) minVal = d.Balance;
      if (d.CashBalanceForecast < minVal) minVal = d.CashBalanceForecast;
      if (d.Inflow > maxVal) maxVal = d.Inflow;
      if (d.Balance > maxVal) maxVal = d.Balance;
      if (d.CashBalanceForecast > maxVal) maxVal = d.CashBalanceForecast;
    });

    const absSpan = Math.max(Math.abs(minVal), Math.abs(maxVal));
    let step = 5000;
    if (absSpan > 200000) step = 50000;
    else if (absSpan > 100000) step = 25000;
    else if (absSpan > 50000) step = 10000;
    else if (absSpan > 20000) step = 5000;
    else if (absSpan > 8000) step = 2500;
    else step = 1000;

    const domainMin = Math.floor(minVal / step) * step - (minVal < 0 ? step : 0);
    const domainMax = Math.ceil(maxVal / step) * step + step;

    const ticks: number[] = [];
    for (let v = domainMin; v <= domainMax; v += step) {
      ticks.push(v);
    }
    if (!ticks.includes(0)) {
      ticks.push(0);
      ticks.sort((a, b) => a - b);
    }

    return { domain: [domainMin, domainMax], ticks };
  }, [trajectoryData]);

  // Rule: balance = available cash - credit card obligations - forecast rule registered
  const immediateBalanceRule = useMemo(() => {
    // Total credit card obligations considered as outflow regardless of paid status
    const ccTotal = cardsForYear.reduce((sum, c) => sum + c.amountToPay, 0);
    const ccPending = creditCardMetrics.totalDebt;
    const forecastRuleOutflow = currentMonthForecastOutflow.monthOutflow;
    const netBalance = activeCashForYear - ccTotal - forecastRuleOutflow;
    const netBalanceWithInflow = netBalance + currentMonthForecastOutflow.monthInflow;

    return {
      availableCash: activeCashForYear,
      ccTotal,
      ccPending,
      forecastRuleOutflow,
      netBalance,
      netBalanceWithInflow
    };
  }, [activeCashForYear, cardsForYear, creditCardMetrics.totalDebt, currentMonthForecastOutflow]);

  // Handlers
  const handleBsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const item: BalanceSheetItem = { 
      id: bsForm.id || Date.now().toString(), 
      name: bsForm.name, 
      type: bsForm.type, 
      value: convertFromInput(parseFloat(bsForm.value) || 0), 
      startDate: bsForm.startDate,
      endDate: bsForm.endDate || undefined,
      date: new Date().toISOString().split('T')[0]
    };
    if (bsForm.id) updateBalanceSheetItem(item); else addBalanceSheetItem(item);
    setBsForm({ id: '', name: '', type: 'Liability', value: '', startDate: new Date().toISOString().split('T')[0], endDate: '' });
  };

  const handleCfSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const event: CashFlowEvent = { 
      id: cfForm.id || Date.now().toString(), 
      description: cfForm.desc, 
      amount: convertFromInput(parseFloat(cfForm.amt) || 0), 
      type: cfForm.type, 
      frequency: 'Monthly', 
      startDate: cfForm.startDate,
      endDate: cfForm.endDate || undefined
    };
    if (cfForm.id) updateCashFlowEvent(event); else addCashFlowEvent(event);
    setCfForm({ id: '', desc: '', amt: '', type: 'Inflow', startDate: new Date().toISOString().split('T')[0], endDate: '' });
  };

  const openBaselineModal = () => {
    setBaselineInput({
      netWorth: janBaseline ? convertToDisplay(janBaseline.netWorth).toFixed(2) : convertToDisplay(currentSnapshot.netWorth).toFixed(2),
      assets: janBaseline ? convertToDisplay(janBaseline.assets).toFixed(2) : convertToDisplay(currentSnapshot.totalAssets).toFixed(2),
      liabilities: janBaseline ? convertToDisplay(janBaseline.liabilities).toFixed(2) : convertToDisplay(currentSnapshot.liabilities).toFixed(2),
      avgInflow: janBaseline ? convertToDisplay(janBaseline.avgInflow).toFixed(2) : convertToDisplay(currentSnapshot.avgInflow).toFixed(2),
      avgOutflow: janBaseline ? convertToDisplay(janBaseline.avgOutflow).toFixed(2) : convertToDisplay(currentSnapshot.avgOutflow).toFixed(2)
    });
    setIsBaselineModalOpen(true);
  };

  const handleSaveBaseline = () => {
    const baseline: JanuaryBaseline = {
      netWorth: convertFromInput(parseFloat(baselineInput.netWorth) || 0),
      assets: convertFromInput(parseFloat(baselineInput.assets) || 0),
      liabilities: convertFromInput(parseFloat(baselineInput.liabilities) || 0),
      avgInflow: convertFromInput(parseFloat(baselineInput.avgInflow) || 0),
      avgOutflow: convertFromInput(parseFloat(baselineInput.avgOutflow) || 0),
      dateSet: new Date().toISOString()
    };
    setJanBaseline(baseline);
    setIsBaselineModalOpen(false);
  };

  return (
    <div className="space-y-12 pb-24 animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 sm:gap-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tighter leading-none">Balance Sheet</h1>
          <p className="text-slate-400 font-bold text-xs sm:text-sm uppercase tracking-widest mt-2">Wealth architecture and cash flow auditing</p>
        </div>
        <div className="flex gap-4">
           <button 
             onClick={openBaselineModal}
             className="bg-white border text-slate-900 px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-slate-100 transition-all border-slate-200 shadow-sm"
           >
             <Zap size={14} fill="currentColor"/> Configure Jan Baseline
           </button>
        </div>
      </header>

      {/* KPI INDICATORS SECTION (Consistent Typography, Font Sizes & Aligned Baselines) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {kpiData.map((kpi) => {
          const change = calcChange(kpi.value, kpi.baseline);
          const isPos = change !== null && change >= 0;
          
          // Invert colors for liabilities and outflow: increase (isPos) is bad (Red)
          const isInverted = kpi.label === 'Total Liabilities' || kpi.label === 'Avg Outflow';
          const colorClass = isInverted 
            ? (isPos ? 'text-rose-500' : 'text-teal-500') 
            : (isPos ? 'text-teal-500' : 'text-rose-500');

          return (
            <div key={kpi.label} className={`p-6 rounded-[32px] border shadow-sm flex flex-col justify-between transition-all group ${isDarkMode ? 'bg-[#111E33] border-[#1A2940] text-white' : 'bg-white border-slate-100 text-slate-900'}`}>
              <div>
                <div className="h-4 flex justify-between items-center mb-2.5">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 leading-none">
                    {kpi.label}
                  </span>
                  {change !== null && (
                    <div className={`flex items-center gap-0.5 text-[10px] font-bold ${colorClass}`}>
                      {change >= 0 ? <ArrowUpRight size={12}/> : <ArrowDownRight size={12}/>}
                      {Math.abs(change).toFixed(1)}%
                    </div>
                  )}
                </div>
                <div className="h-8 flex items-center mb-1">
                  <h3 className="font-['Sora',sans-serif] text-xl sm:text-2xl font-bold tracking-tight leading-none truncate">
                    {currency} {convertToDisplay(kpi.value).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </h3>
                </div>
                <div className="h-4 flex items-center">
                  <p className="text-[10px] font-medium text-slate-400 uppercase tracking-widest truncate">
                    vs Jan Baseline
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* SOLVENCY & CASH FLOW HEALTH SCORECARD */}
      <div className="bg-white rounded-[32px] sm:rounded-[36px] p-6 sm:p-8 border border-slate-100 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
          <div>
            <h3 className="text-base font-black text-slate-900 tracking-tight flex items-center gap-2">
              <ShieldCheck size={20} className="text-teal-600" /> Solvency & Capital Velocity Scorecard
            </h3>
            <p className="text-xs text-slate-400 font-medium mt-0.5">Key diagnostic indicators measuring structural balance sheet resilience</p>
          </div>
          <span className="text-[10px] font-black uppercase tracking-wider px-3 py-1 bg-slate-100 text-slate-600 rounded-full w-fit">
            Diagnostic Health: {currentSnapshot.solvencyRatio >= 70 ? 'Optimal Solvency' : 'Balanced'}
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="p-4 bg-slate-50/80 rounded-2xl border border-slate-100">
            <div className="text-[9.5px] font-black uppercase tracking-wider text-slate-400 mb-1">Solvency (Equity)</div>
            <div className="text-xl font-black text-teal-600 tracking-tight">{currentSnapshot.solvencyRatio.toFixed(1)}%</div>
            <div className="text-[9.5px] font-bold text-slate-400 mt-1">Equity cushion (&gt;70% optimal)</div>
          </div>

          <div className="p-4 bg-slate-50/80 rounded-2xl border border-slate-100">
            <div className="text-[9.5px] font-black uppercase tracking-wider text-slate-400 mb-1">Debt-to-Assets</div>
            <div className="text-xl font-black text-rose-500 tracking-tight">{currentSnapshot.debtRatio.toFixed(1)}%</div>
            <div className="text-[9.5px] font-bold text-slate-400 mt-1">Leverage ratio (&lt;30% safe)</div>
          </div>

          <div className="p-4 bg-slate-50/80 rounded-2xl border border-slate-100">
            <div className="text-[9.5px] font-black uppercase tracking-wider text-slate-400 mb-1">Liquid Cash Runway</div>
            <div className="text-xl font-black text-slate-900 tracking-tight">{currentSnapshot.cashRunway.toFixed(1)} Mo</div>
            <div className="text-[9.5px] font-bold text-slate-400 mt-1">{currency} {convertToDisplay(currentSnapshot.manualAssets).toLocaleString()} cash buffer</div>
          </div>

          <div className="p-4 bg-slate-50/80 rounded-2xl border border-slate-100">
            <div className="text-[9.5px] font-black uppercase tracking-wider text-slate-400 mb-1">Passive Coverage</div>
            <div className="text-xl font-black text-teal-600 tracking-tight">{currentSnapshot.passiveCoverage.toFixed(1)}%</div>
            <div className="text-[9.5px] font-bold text-slate-400 mt-1">Expenses covered by dividends</div>
          </div>

          <div className="p-4 bg-slate-50/80 rounded-2xl border border-slate-100 col-span-2 sm:col-span-1">
            <div className="text-[9.5px] font-black uppercase tracking-wider text-slate-400 mb-1">Asset-to-Debt Coverage</div>
            <div className="text-xl font-black text-blue-600 tracking-tight">{currentSnapshot.assetCoverage.toFixed(1)}x</div>
            <div className="text-[9.5px] font-bold text-slate-400 mt-1">Asset backing per $ debt</div>
          </div>
        </div>
      </div>

      {/* AVAILABLE CASH & LIQUIDITY BALANCE ENGINE */}
      {/* Rule: balance = available cash - credit card pending - forecast rule registered */}
      <div className={`rounded-[32px] sm:rounded-[40px] border shadow-sm p-6 sm:p-8 space-y-5 transition-all ${isDarkMode ? 'bg-[#111E33] border-[#1A2940]' : 'bg-white border-slate-100'}`}>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-4 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-2xl">
              <Wallet size={24} />
            </div>
            <div>
              <div className="flex items-center gap-2.5">
                <h3 className="text-xl font-black tracking-tight text-slate-900 dark:text-white">Available Cash & Balance Engine</h3>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400">
                  Live Liquidity Rule
                </span>
              </div>
              <p className="text-xs text-slate-400 font-medium mt-0.5">
                Dynamic balance rule: <strong className="text-slate-700 dark:text-slate-300">Balance = Available Cash − Credit Card Pending − Forecast Rules Registered</strong>
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={handleSyncLedgerCash}
              className={`px-4 py-2.5 rounded-2xl text-xs font-black uppercase tracking-wider transition-all border flex items-center gap-2 cursor-pointer shadow-xs active:scale-95 ${
                isDarkMode ? 'bg-[#0D1625] border-blue-500/40 text-blue-400 hover:bg-blue-900/20 hover:text-white' : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:text-blue-900'
              }`}
              title="Register timestamp, update account ledger cash amount of current, and keep track of previous on the history of the ledger"
            >
              <RefreshCcw size={14} className={syncFeedback ? 'animate-spin text-emerald-500' : ''} /> Sync Ledger Cash
            </button>
          </div>
        </div>

        {/* Sync Ledger Feedback Confirmation */}
        {syncFeedback && (
          <div className="p-3.5 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 text-xs font-bold flex items-center justify-between gap-3 animate-in fade-in duration-300">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
              <span>
                <strong>Cash Ledger Synchronized ({syncFeedback.timestamp}):</strong> Current account ledger cash updated to {currency} {convertToDisplay(syncFeedback.amount).toLocaleString()} for {syncFeedback.monthName}.
                {syncFeedback.previous !== undefined && ` Previous recorded: ${currency} ${convertToDisplay(syncFeedback.previous).toLocaleString()} kept in history.`}
              </span>
            </div>
            <button onClick={() => setSyncFeedback(null)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"><X size={14}/></button>
          </div>
        )}

        {/* Real-time Equation Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-stretch">
          {/* Box 1: Available Cash Input Box */}
          <div className={`p-5 rounded-2xl border transition-all flex flex-col justify-between ${
            isDarkMode ? 'bg-[#0D1625] border-blue-500/40 shadow-blue-500/5' : 'bg-blue-50/40 border-blue-200 shadow-sm'
          }`}>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-black uppercase tracking-widest text-blue-600 dark:text-blue-400">
                  Available Cash (Input)
                </span>
                <span className="text-[10px] font-bold text-slate-400">Current Reserves</span>
              </div>
              
              <div className="relative mt-2">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 font-['Sora',sans-serif] font-bold text-slate-400 text-base">
                  {currency}
                </span>
                <input
                  type="number"
                  step="any"
                  value={cashInput}
                  onChange={(e) => handleCashChange(e.target.value)}
                  className={`w-full pl-8 pr-3 py-2.5 rounded-xl font-['Sora',sans-serif] text-xl font-bold tracking-tight transition-all outline-hidden border ${
                    isDarkMode 
                      ? 'bg-[#162340] border-[#1A2940] text-white focus:border-blue-500' 
                      : 'bg-white border-blue-300 text-slate-900 focus:border-blue-600 focus:ring-2 focus:ring-blue-100'
                  }`}
                  placeholder="0"
                />
              </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-3 font-medium">
              Insert your liquid bank cash to update balances and projected trajectory in real time.
            </p>
          </div>

          {/* Box 2: Minus Credit Card Outflow */}
          <div className={`p-5 rounded-2xl border flex flex-col justify-between ${
            isDarkMode ? 'bg-[#0D1625] border-rose-500/30' : 'bg-rose-50/40 border-rose-200'
          }`}>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-black uppercase tracking-widest text-rose-600 dark:text-rose-400 flex items-center gap-1">
                  <span>−</span> Credit Card Outflow
                </span>
                <span className="px-2 py-0.5 rounded-md text-[9px] font-bold bg-rose-100 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400">
                  {creditCards.length} cards
                </span>
              </div>
              <p className="font-['Sora',sans-serif] text-xl sm:text-2xl font-bold text-rose-600 dark:text-rose-400 mt-2">
                −{currency} {convertToDisplay(immediateBalanceRule.ccTotal).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
            </div>
            <p className="text-[10px] text-slate-400 mt-3 font-medium">
              Credit card obligations considered as outflow (regardless of paid status).
            </p>
          </div>

          {/* Box 3: Minus Forecast Rule Registered */}
          <div className={`p-5 rounded-2xl border flex flex-col justify-between ${
            isDarkMode ? 'bg-[#0D1625] border-amber-500/30' : 'bg-amber-50/40 border-amber-200'
          }`}>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-black uppercase tracking-widest text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <span>−</span> Forecast Rules Registered
                </span>
                <span className="px-2 py-0.5 rounded-md text-[9px] font-bold bg-amber-100 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400">
                  {currentMonthForecastOutflow.activeCount} rules
                </span>
              </div>
              <p className="font-['Sora',sans-serif] text-xl sm:text-2xl font-bold text-amber-600 dark:text-amber-400 mt-2">
                −{currency} {convertToDisplay(immediateBalanceRule.forecastRuleOutflow).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
            </div>
            <p className="text-[10px] text-slate-400 mt-3 font-medium">
              Active recurring forecast outflow rules registered.
            </p>
          </div>

          {/* Box 4: Result: Updated Balance (Highlighted) */}
          <div className={`p-5 rounded-2xl border flex flex-col justify-between ${
            immediateBalanceRule.netBalance >= 0 
              ? (isDarkMode ? 'bg-emerald-950/20 border-emerald-500/40' : 'bg-emerald-50/60 border-emerald-300')
              : (isDarkMode ? 'bg-rose-950/20 border-rose-500/40' : 'bg-rose-50/60 border-rose-300')
          }`}>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className={`text-[10px] font-black uppercase tracking-widest flex items-center gap-1 ${
                  immediateBalanceRule.netBalance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                }`}>
                  <span>=</span> Updated Balance
                </span>
                <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase ${
                  immediateBalanceRule.netBalance >= 0 ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300' : 'bg-rose-100 dark:bg-rose-900/50 text-rose-700 dark:text-rose-300'
                }`}>
                  {immediateBalanceRule.netBalance >= 0 ? 'Liquid Buffer' : 'Deficit Alert'}
                </span>
              </div>
              <p className={`font-['Sora',sans-serif] text-xl sm:text-2xl font-bold mt-2 ${
                immediateBalanceRule.netBalance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
              }`}>
                {immediateBalanceRule.netBalance < 0 ? '−' : ''}{currency} {convertToDisplay(Math.abs(immediateBalanceRule.netBalance)).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
            </div>
            
            <div className={`mt-3 pt-2.5 border-t text-[10px] flex items-center justify-between font-bold ${
              isDarkMode ? 'border-slate-800 text-slate-300' : 'border-slate-200 text-slate-600'
            }`}>
              <span>With Inflow (+{currency}{convertToDisplay(immediateBalanceRule.netBalanceWithInflow - immediateBalanceRule.netBalance).toLocaleString(undefined, { maximumFractionDigits: 0 })}):</span>
              <span className="font-['Sora',sans-serif] text-slate-900 dark:text-white text-xs">
                {currency} {convertToDisplay(immediateBalanceRule.netBalanceWithInflow).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Cash Flow & Balance */}
      <div className="space-y-6">
        {/* MONTHLY CASH FLOW & BALANCE GRAPH */}
        <div className={`rounded-[32px] sm:rounded-[40px] border shadow-sm p-6 sm:p-10 overflow-hidden space-y-6 transition-all ${isDarkMode ? 'bg-[#111E33] border-[#1A2940]' : 'bg-white border-slate-100'}`}>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-black tracking-tight text-slate-900 dark:text-white flex items-center gap-3">
                  <Activity size={24} className="text-blue-600"/> Monthly Cash Flow & Balance
                </h3>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-black bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300">
                  {selectedYear}
                </span>
              </div>
              <p className="text-sm text-slate-400 font-medium">
                Monthly inflows and outflows with projected cash balance for {selectedYear}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex flex-wrap items-center gap-4 text-[10px] font-black uppercase tracking-wider">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-xs bg-emerald-500"></div> Monthly Inflow
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-xs bg-rose-500"></div> Monthly Outflow
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-1 bg-sky-500 rounded-full"></div> Cash Balance
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-1 border-b-2 border-dashed border-amber-500"></div> Cash Balance Forecast
                </div>
              </div>
            </div>
          </div>

          <div className="h-96 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={trajectoryData} margin={{ top: 20, right: 20, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={P.border} />
                <XAxis 
                  dataKey="name" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 11, fontWeight: 700, fill: P.subtle }} 
                />
                {/* Single Y-Axis on the left: Monetary value of inflows, outflows, and cash balance */}
                <YAxis 
                  domain={yAxisConfig.domain}
                  ticks={yAxisConfig.ticks}
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 10, fill: P.muted, fontWeight: 600 }} 
                  tickFormatter={v => {
                    if (v === 0) return '0';
                    const sign = v < 0 ? '−' : '+';
                    const abs = Math.abs(v);
                    return `${sign}${(abs / 1000).toFixed(0)}k`;
                  }} 
                />
                <Tooltip content={<DarkTip />} cursor={{ stroke: P.border, strokeWidth: 1 }} />

                {/* Green bar represents total monthly inflow from forecast rules (starts at 0 and extends upward) */}
                <Bar 
                  dataKey="Inflow" 
                  name="Monthly Inflow" 
                  fill="#10b981" 
                  radius={[6, 6, 0, 0]}
                  maxBarSize={32}
                >
                  {trajectoryData.map((entry, index) => (
                    <Cell key={`inflow-cell-${index}`} fill="#10b981" />
                  ))}
                </Bar>

                {/* Red bar represents total outflow (credit card + forecast outflows) starting at 0 and extending downward */}
                <Bar 
                  dataKey="TotalOutflowNegative" 
                  name="Monthly Outflow" 
                  fill="#ef4444" 
                  radius={[0, 0, 6, 6]}
                  maxBarSize={32}
                >
                  {trajectoryData.map((entry, index) => (
                    <Cell key={`outflow-cell-${index}`} fill="#ef4444" />
                  ))}
                </Bar>

                {/* Clearly visible Zero Baseline drawn on top of the bars */}
                <ReferenceLine 
                  y={0} 
                  stroke={isDarkMode ? "#94a3b8" : "#475569"} 
                  strokeWidth={2.5}
                  label={{
                    value: '0',
                    position: 'insideLeft',
                    fill: isDarkMode ? '#cbd5e1' : '#334155',
                    fontSize: 11,
                    fontWeight: 800,
                    offset: 5
                  }}
                />

                {/* Monthly cash balance trend line */}
                <Line 
                  type="monotone" 
                  dataKey="Balance" 
                  name="Cash Balance" 
                  stroke="#0284c7" 
                  strokeWidth={3.5} 
                  dot={{ r: 4.5, fill: '#0284c7', strokeWidth: 2, stroke: isDarkMode ? '#111E33' : '#ffffff' }} 
                  activeDot={{ r: 6.5, fill: '#38bdf8', strokeWidth: 2, stroke: isDarkMode ? '#111E33' : '#ffffff' }} 
                />

                {/* Trend line of Cash Balance Forecast */}
                <Line 
                  type="monotone" 
                  dataKey="CashBalanceForecast" 
                  name="Cash Balance Forecast" 
                  stroke="#f59e0b" 
                  strokeWidth={2.5} 
                  strokeDasharray="6 4"
                  dot={{ r: 4, fill: '#f59e0b', strokeWidth: 2, stroke: isDarkMode ? '#111E33' : '#ffffff' }} 
                  activeDot={{ r: 6.5, fill: '#fbbf24', strokeWidth: 2, stroke: isDarkMode ? '#111E33' : '#ffffff' }} 
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* ══ CREDIT CARDS & CURRENT LIABILITIES TRACKER ══ */}
      <div className={`rounded-[32px] sm:rounded-[40px] border shadow-sm p-6 sm:p-10 space-y-6 transition-all ${isDarkMode ? 'bg-[#111E33] border-[#1A2940]' : 'bg-white border-slate-100'}`}>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-4 border-b border-slate-100 dark:border-slate-800">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-2xl">
                <CreditCard size={22} />
              </div>
              <h3 className="text-xl font-black tracking-tight text-slate-900 dark:text-white">Credit Cards & Current Liabilities</h3>
              <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400">
                Amount to Pay: −{currency} {convertToDisplay(creditCardMetrics.totalDebt).toLocaleString()}
              </span>
            </div>
            <p className="text-xs text-slate-400 font-medium mt-1">
              Keep track of active card statement balances, amounts to pay, and upcoming payment deadlines to manage current liabilities.
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Filter pills */}
            <div className={`p-1 rounded-2xl border flex items-center gap-1 ${isDarkMode ? 'bg-[#0D1625] border-[#1A2940]' : 'bg-slate-100 border-slate-200'}`}>
              {(['all', 'unpaid', 'paid'] as const).map(filter => (
                <button
                  key={filter}
                  onClick={() => setCcFilter(filter)}
                  className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
                    ccFilter === filter
                      ? 'bg-slate-900 text-white shadow-xs'
                      : 'text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                  }`}
                >
                  {filter === 'all' ? `All (${creditCards.length})` : filter === 'unpaid' ? `Unpaid (${creditCardMetrics.unpaidCount})` : 'Paid'}
                </button>
              ))}
            </div>

            <button
              onClick={openAddCardModal}
              className="px-5 py-3 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-wider shadow-lg shadow-slate-200 dark:shadow-none hover:bg-blue-600 transition-all flex items-center gap-2 cursor-pointer active:scale-95"
            >
              <Plus size={16} /> Add Credit Card
            </button>
          </div>
        </div>

        {/* Current Liabilities Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-[#0D1625] border-[#1A2940]' : 'bg-slate-50/80 border-slate-100'}`}>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">Total Amount to Pay</span>
            <p className="font-['Sora',sans-serif] text-lg sm:text-xl font-bold text-rose-600 dark:text-rose-400 leading-none">
              −{currency} {convertToDisplay(creditCardMetrics.totalDebt).toLocaleString()}
            </p>
            <span className="text-[10px] text-slate-400 font-medium mt-1 block">Active Unpaid Liabilities</span>
          </div>

          <div className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-[#0D1625] border-[#1A2940]' : 'bg-slate-50/80 border-slate-100'}`}>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">Next Payment Deadline</span>
            <div className="flex items-center gap-1.5">
              <CalendarClock size={16} className={creditCardMetrics.daysUntilNext !== null && creditCardMetrics.daysUntilNext <= 5 ? 'text-amber-500' : 'text-blue-500'} />
              <p className="font-['Sora',sans-serif] text-sm sm:text-base font-bold text-slate-900 dark:text-white leading-none">
                {creditCardMetrics.nextCard ? creditCardMetrics.nextCard.deadline : 'None pending'}
              </p>
            </div>
            <span className="text-[10px] text-slate-400 font-medium mt-1 block">
              {creditCardMetrics.daysUntilNext !== null 
                ? (creditCardMetrics.daysUntilNext <= 0 ? 'Due today / overdue' : `Due in ${creditCardMetrics.daysUntilNext} days`)
                : 'All obligations settled'
              }
            </span>
          </div>

          <div className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-[#0D1625] border-[#1A2940]' : 'bg-slate-50/80 border-slate-100'}`}>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">Minimum Payment Due</span>
            <p className="font-['Sora',sans-serif] text-lg sm:text-xl font-bold text-slate-900 dark:text-white leading-none">
              {currency} {convertToDisplay(creditCardMetrics.totalMinDue).toLocaleString()}
            </p>
            <span className="text-[10px] text-slate-400 font-medium mt-1 block">Required to maintain standing</span>
          </div>

          <div className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-[#0D1625] border-[#1A2940]' : 'bg-slate-50/80 border-slate-100'}`}>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">Credit Utilization</span>
            <p className="font-['Sora',sans-serif] text-lg sm:text-xl font-bold text-teal-600 dark:text-teal-400 leading-none">
              {creditCardMetrics.utilizationRate.toFixed(1)}%
            </p>
            <span className="text-[10px] text-slate-400 font-medium mt-1 block">
              Limit: {currency} {convertToDisplay(creditCardMetrics.totalLimit).toLocaleString()}
            </span>
          </div>
        </div>

        {/* Credit Cards Grid */}
        {filteredCards.length === 0 ? (
          <div className={`p-10 rounded-3xl border-2 border-dashed flex flex-col items-center justify-center text-center ${isDarkMode ? 'border-[#1A2940] text-slate-500' : 'border-slate-200 text-slate-400'}`}>
            <CreditCard size={36} className="mb-3 opacity-40" />
            <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200">No credit cards in this view</h4>
            <p className="text-xs text-slate-400 mt-1 max-w-sm">
              {ccFilter === 'paid' ? 'No settled credit cards found.' : 'Add your credit cards to keep track of payment deadlines and monthly amounts to pay.'}
            </p>
            {ccFilter !== 'paid' && (
              <button onClick={openAddCardModal} className="mt-4 px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-wider hover:bg-blue-600 transition-all cursor-pointer">
                + Add Card
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredCards.map(card => {
              const status = getDeadlineStatus(card.deadline, card.isPaid);
              const toneClasses = {
                paid: 'bg-teal-50 dark:bg-teal-950/40 text-teal-600 dark:text-teal-400 border-teal-200/50',
                overdue: 'bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 border-rose-200/50',
                urgent: 'bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 border-rose-200/50',
                soon: 'bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 border-amber-200/50',
                upcoming: 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200/50'
              }[status.tone];

              return (
                <div 
                  key={card.id}
                  className={`p-6 rounded-3xl border transition-all flex flex-col justify-between group hover:shadow-md ${
                    isDarkMode ? 'bg-[#0D1625] border-[#1A2940]' : 'bg-slate-50/70 border-slate-100 hover:border-blue-200'
                  }`}
                >
                  <div className="space-y-4">
                    {/* Top Row: Issuer & Status Badge */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-1 bg-slate-900 text-white text-[9px] font-black uppercase tracking-wider rounded-lg">
                          {card.issuer || 'Card'}
                        </span>
                        {card.last4 && (
                          <span className="text-[11px] font-mono font-bold text-slate-400">
                            •••• {card.last4}
                          </span>
                        )}
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border flex items-center gap-1 ${toneClasses}`}>
                        {status.tone === 'paid' && <Check size={11} />}
                        {status.tone === 'overdue' && <AlertTriangle size={11} />}
                        {status.tone === 'soon' && <Clock size={11} />}
                        {status.label}
                      </span>
                    </div>

                    {/* Card Name */}
                    <div>
                      <h4 className="font-bold text-base text-slate-900 dark:text-white leading-tight">
                        {card.name}
                      </h4>
                      {card.notes && (
                        <p className="text-[11px] text-slate-400 truncate mt-0.5">{card.notes}</p>
                      )}
                    </div>

                    {/* Amount to Pay & Deadline Highlight */}
                    <div className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-[#111E33] border-[#1A2940]' : 'bg-white border-slate-100 shadow-2xs'}`}>
                      <div className="flex justify-between items-end">
                        <div>
                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-0.5">
                            Amount to Pay
                          </span>
                          <span className={`font-['Sora',sans-serif] text-xl font-bold leading-none ${card.isPaid ? 'text-slate-400 line-through' : 'text-slate-900 dark:text-white'}`}>
                            −{currency} {convertToDisplay(card.amountToPay).toLocaleString()}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-0.5">
                            Deadline
                          </span>
                          <span className="text-xs font-bold font-mono text-slate-700 dark:text-slate-300">
                            {card.deadline}
                          </span>
                        </div>
                      </div>

                      {/* Card Meta row: Min due & limit */}
                      <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 text-[10px]">
                        <div>
                          <span className="text-slate-400">Min due: </span>
                          <strong className="text-slate-700 dark:text-slate-300">
                            {currency} {convertToDisplay(card.minimumPayment || 0).toLocaleString()}
                          </strong>
                        </div>
                        <div className="text-right">
                          <span className="text-slate-400">Limit: </span>
                          <strong className="text-slate-700 dark:text-slate-300">
                            {card.creditLimit ? `${currency} ${convertToDisplay(card.creditLimit).toLocaleString()}` : '--'}
                          </strong>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions footer */}
                  <div className="flex items-center justify-between gap-2 pt-4 mt-2 border-t border-slate-200/60 dark:border-slate-800">
                    <button
                      onClick={() => toggleCreditCardPaid(card.id)}
                      className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all flex items-center gap-1.5 cursor-pointer ${
                        card.isPaid
                          ? 'bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-300'
                          : 'bg-teal-600 text-white hover:bg-teal-700 shadow-xs'
                      }`}
                    >
                      <Check size={12} />
                      {card.isPaid ? 'Mark as Unpaid' : 'Mark as Paid'}
                    </button>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEditCardModal(card)}
                        className="p-2 text-slate-400 hover:text-blue-600 rounded-xl hover:bg-white dark:hover:bg-slate-800 transition-all cursor-pointer"
                        title="Edit Credit Card"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteCreditCard(card.id);
                        }}
                        className="p-2 text-slate-400 hover:text-rose-600 rounded-xl hover:bg-white dark:hover:bg-slate-800 transition-all cursor-pointer"
                        title="Delete Credit Card"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Ledger Management Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        
        {/* Account Ledger Section */}
        <div className="space-y-8">
           <div className="bg-white p-6 sm:p-10 rounded-[32px] sm:rounded-[40px] border border-slate-100 shadow-sm space-y-8">
              <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-3">
                 <Briefcase size={18} className="text-blue-500"/> {bsForm.id ? 'Edit Ledger Entry' : 'Add to Account Ledger'}
              </h3>
              <form onSubmit={handleBsSubmit} className="space-y-6">
                 <input required placeholder="Account/Item Name" value={bsForm.name} onChange={e=>setBsForm({...bsForm, name: e.target.value})} className="w-full p-5 bg-slate-50 rounded-3xl text-sm font-bold outline-none ring-2 ring-transparent focus:ring-blue-500 transition-all" />
                 <div className="grid grid-cols-2 gap-4">
                    <select value={bsForm.type} onChange={e=>setBsForm({...bsForm, type: e.target.value as any})} className="p-5 bg-slate-50 rounded-3xl text-sm font-bold outline-none appearance-none">
                       <option value="Liability">Liability (-)</option><option value="Asset">Asset (+)</option>
                    </select>
                    <div className="relative">
                       <span className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 font-black">{currency}</span>
                       <input type="number" step="0.01" required placeholder="Total Value" value={bsForm.value} onChange={e=>setBsForm({...bsForm, value: e.target.value})} className="w-full pl-16 pr-5 py-5 bg-slate-50 rounded-3xl text-sm font-black outline-none ring-2 ring-transparent focus:ring-blue-500 transition-all" />
                    </div>
                 </div>
                 <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                       <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-2 flex items-center gap-1"><Calendar size={12}/> Start Date</label>
                       <input type="date" required value={bsForm.startDate} onChange={e=>setBsForm({...bsForm, startDate: e.target.value})} className="w-full p-4 bg-slate-50 rounded-2xl text-xs font-bold outline-none" />
                    </div>
                    <div className="space-y-1">
                       <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-2 flex items-center gap-1"><Calendar size={12}/> End Date (Optional)</label>
                       <input type="date" value={bsForm.endDate} onChange={e=>setBsForm({...bsForm, endDate: e.target.value})} className="w-full p-4 bg-slate-50 rounded-2xl text-xs font-bold outline-none" />
                    </div>
                 </div>
                 <div className="flex gap-4">
                    {bsForm.id && <button type="button" onClick={() => setBsForm({ id: '', name: '', type: 'Liability', value: '', startDate: new Date().toISOString().split('T')[0], endDate: '' })} className="p-5 bg-slate-100 text-slate-400 rounded-3xl hover:bg-slate-200 transition-all"><X size={20}/></button>}
                    <button className="flex-1 bg-slate-900 text-white font-black py-5 rounded-3xl shadow-xl shadow-slate-200 uppercase tracking-widest text-xs flex items-center justify-center gap-2 hover:bg-blue-600 transition-all">
                       <Save size={16}/> {bsForm.id ? 'Save Changes' : 'Register Entry'}
                    </button>
                 </div>
              </form>
           </div>

           <div className="space-y-4">
              <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 ml-4"><History size={16}/> Ledger History ({selectedYear})</h3>
              {displayedBalanceSheet.length === 0 && (
                <div className="bg-white/50 p-12 rounded-[40px] border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-300">
                  <Briefcase size={32} className="mb-4 opacity-50"/>
                  <p className="text-[10px] font-black uppercase tracking-widest">No entries found for {selectedYear}</p>
                </div>
              )}
              {displayedBalanceSheet.map(item => (
                <div key={item.id} className="bg-white p-6 rounded-[32px] border border-slate-100 flex justify-between items-center group shadow-sm hover:border-blue-200 transition-all">
                   <div className="flex gap-4 items-center">
                      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${item.type === 'Asset' ? 'bg-teal-50 text-teal-500' : 'bg-rose-50 text-rose-500'}`}>{item.type === 'Asset' ? <ArrowUpRight size={18}/> : <ArrowDownRight size={18}/>}</div>
                      <div>
                        <h4 className="font-black text-slate-800 text-sm">{item.name}</h4>
                        <div className="flex items-center gap-2">
                           <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase ${item.endDate && new Date(item.endDate) < new Date() ? 'bg-slate-100 text-slate-400' : 'bg-teal-50 text-teal-600'}`}>
                              {item.endDate && new Date(item.endDate) < new Date() ? 'Ended' : 'Active'}
                           </span>
                           <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                             {item.startDate} {item.endDate ? `→ ${item.endDate}` : '(Ongoing)'}
                           </p>
                        </div>
                      </div>
                   </div>
                   <div className="flex items-center gap-4">
                      <p className={`font-black ${item.type === 'Asset' ? 'text-teal-600' : 'text-rose-600'}`}>{currency} {convertToDisplay(item.value).toLocaleString()}</p>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                         <button onClick={() => setBsForm({ id: item.id, name: item.name, type: item.type, value: convertToDisplay(item.value).toString(), startDate: item.startDate, endDate: item.endDate || '' })} className="p-2 text-slate-300 hover:text-blue-600 transition-all"><Edit2 size={14}/></button>
                         <button onClick={() => deleteBalanceSheetItem(item.id)} className="p-2 text-slate-300 hover:text-rose-500 transition-all"><Trash2 size={14}/></button>
                      </div>
                   </div>
                </div>
              ))}
           </div>
        </div>

        {/* Cash Flow Section */}
        <div className="space-y-8">
           <div className="bg-white p-6 sm:p-10 rounded-[32px] sm:rounded-[40px] border border-slate-100 shadow-sm space-y-8">
              <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-3">
                 <Clock size={18} className="text-teal-500"/> {cfForm.id ? 'Edit Forecast Rule' : 'New Forecast Rule'}
              </h3>
              <form onSubmit={handleCfSubmit} className="space-y-6">
                 <input required placeholder="Source Description" value={cfForm.desc} onChange={e=>setCfForm({...cfForm, desc: e.target.value})} className="w-full p-5 bg-slate-50 rounded-3xl text-sm font-bold outline-none ring-2 ring-transparent focus:ring-teal-500 transition-all" />
                 <div className="grid grid-cols-2 gap-4">
                    <select value={cfForm.type} onChange={e=>setCfForm({...cfForm, type: e.target.value as any})} className="p-5 bg-slate-50 rounded-3xl text-sm font-bold outline-none appearance-none">
                       <option value="Inflow">Income (+)</option><option value="Outflow">Bill / Cost (-)</option>
                    </select>
                    <div className="relative">
                       <span className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 font-black">{currency}</span>
                       <input type="number" step="0.01" required placeholder="Monthly Amount" value={cfForm.amt} onChange={e=>setCfForm({...cfForm, amt: e.target.value})} className="w-full pl-16 pr-5 py-5 bg-slate-50 rounded-3xl text-sm font-black outline-none ring-2 ring-transparent focus:ring-teal-500 transition-all" />
                    </div>
                 </div>
                 <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                       <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-2 flex items-center gap-1"><Calendar size={12}/> Start Date</label>
                       <input type="date" required value={cfForm.startDate} onChange={e=>setCfForm({...cfForm, startDate: e.target.value})} className="w-full p-4 bg-slate-50 rounded-2xl text-xs font-bold outline-none" />
                    </div>
                    <div className="space-y-1">
                       <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-2 flex items-center gap-1"><Calendar size={12}/> Finish Date (Optional)</label>
                       <input type="date" value={cfForm.endDate} onChange={e=>setCfForm({...cfForm, endDate: e.target.value})} className="w-full p-4 bg-slate-50 rounded-2xl text-xs font-bold outline-none" />
                    </div>
                 </div>
                 <div className="flex gap-4">
                    {cfForm.id && <button type="button" onClick={() => setCfForm({ id: '', desc: '', amt: '', type: 'Inflow', startDate: new Date().toISOString().split('T')[0], endDate: '' })} className="p-5 bg-slate-100 text-slate-400 rounded-3xl hover:bg-slate-200 transition-all"><X size={20}/></button>}
                    <button className="flex-1 bg-teal-600 text-white font-black py-5 rounded-3xl shadow-xl shadow-teal-50 uppercase tracking-widest text-xs flex items-center justify-center gap-2 hover:bg-slate-900 transition-all">
                       <Save size={16}/> {cfForm.id ? 'Update Rule' : 'Project into Trajectory'}
                    </button>
                 </div>
              </form>
           </div>

           <div className="space-y-4">
              <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 ml-4"><History size={16}/> Forecast History ({selectedYear})</h3>
              {displayedCashFlowEvents.length === 0 && (
                <div className="bg-white/50 p-12 rounded-[40px] border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-300">
                  <Clock size={32} className="mb-4 opacity-50"/>
                  <p className="text-[10px] font-black uppercase tracking-widest">No projections set for {selectedYear}</p>
                </div>
              )}
              {displayedCashFlowEvents.map(ev => (
                <div key={ev.id} className="bg-white p-6 rounded-[32px] border border-slate-100 flex justify-between items-center group shadow-sm transition-all hover:border-teal-200">
                   <div className="flex gap-4 items-center">
                      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${ev.type === 'Inflow' ? 'bg-teal-50 text-teal-500' : 'bg-amber-50 text-amber-500'}`}><Clock size={18}/></div>
                      <div>
                        <h4 className="font-black text-slate-800 text-sm">{ev.description}</h4>
                        <div className="flex items-center gap-2">
                           <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase ${ev.endDate && new Date(ev.endDate) < new Date() ? 'bg-slate-100 text-slate-400' : 'bg-blue-50 text-blue-600'}`}>
                              {ev.endDate && new Date(ev.endDate) < new Date() ? 'Ended' : 'Active'}
                           </span>
                           <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                             {ev.startDate} {ev.endDate ? `→ ${ev.endDate}` : '(Recurring)'}
                           </p>
                        </div>
                      </div>
                   </div>
                   <div className="flex items-center gap-4">
                      <p className={`font-black ${ev.type === 'Inflow' ? 'text-teal-600' : 'text-rose-600'}`}>{ev.type === 'Inflow' ? '+' : '-'}{currency} {convertToDisplay(ev.amount).toLocaleString()}</p>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                         <button onClick={() => setCfForm({ id: ev.id, desc: ev.description, amt: convertToDisplay(ev.amount).toString(), type: ev.type, startDate: ev.startDate, endDate: ev.endDate || '' })} className="p-2 text-slate-300 hover:text-blue-600 transition-all"><Edit2 size={14}/></button>
                         <button onClick={() => deleteCashFlowEvent(ev.id)} className="p-2 text-slate-300 hover:text-rose-500 transition-all"><Trash2 size={14}/></button>
                      </div>
                   </div>
                </div>
              ))}
           </div>
        </div>
      </div>

      {/* ══ HISTORICAL LEDGERS & AUDIT TRAIL (Positioned at bottom of page) ══ */}
      {/* Keeps track of every month when a credit card is paid and cash available updates with timestamps */}
      <div className={`rounded-[32px] sm:rounded-[40px] border shadow-sm p-6 sm:p-10 space-y-6 transition-all ${isDarkMode ? 'bg-[#111E33] border-[#1A2940]' : 'bg-white border-slate-100'}`}>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-4 border-b border-slate-100 dark:border-slate-800">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-2xl">
                <History size={22} />
              </div>
              <div>
                <div className="flex items-center gap-2.5">
                  <h3 className="text-xl font-black tracking-tight text-slate-900 dark:text-white">
                    Historical Ledgers & Audit Engine
                  </h3>
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400">
                    Live Audit Trail
                  </span>
                </div>
                <p className="text-xs text-slate-400 font-medium mt-0.5">
                  Register of monthly credit card settlements and timestamped available cash balances driving the monthly balance formula.
                </p>
              </div>
            </div>
          </div>

          {/* Tab Switcher between Cash Ledger History and Credit Card Settlement Ledger */}
          <div className="flex items-center gap-2">
            <div className={`p-1 rounded-2xl border flex items-center gap-1 ${isDarkMode ? 'bg-[#0D1625] border-[#1A2940]' : 'bg-slate-100 border-slate-200'}`}>
              <button
                onClick={() => setActiveLedgerTab('cashLedger')}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-1.5 ${
                  activeLedgerTab === 'cashLedger'
                    ? 'bg-slate-900 text-white shadow-xs'
                    : 'text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                }`}
              >
                <Wallet size={13} />
                Cash History ({cashLedgerHistory.length})
              </button>
              <button
                onClick={() => setActiveLedgerTab('creditCardLedger')}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-1.5 ${
                  activeLedgerTab === 'creditCardLedger'
                    ? 'bg-slate-900 text-white shadow-xs'
                    : 'text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                }`}
              >
                <CreditCard size={13} />
                Credit Card Settlements ({creditCardLedger.length})
              </button>
            </div>
          </div>
        </div>

        {/* Tab 1: Cash Ledger History */}
        {activeLedgerTab === 'cashLedger' && (
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row justify-between md:items-center gap-3 text-xs text-slate-400 font-medium pb-2 border-b border-slate-100 dark:border-slate-800">
              <div className="space-y-1">
                <span>
                  Timestamped records of liquid available cash. For each specific month, the <strong>last historical amount</strong> recorded is considered directly in the balance formula.
                </span>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 pt-0.5">
                  Showing historical records for <strong>{selectedYear}</strong>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <button
                  onClick={() => {
                    setPlanCashForm({ monthIndex: 0, year: selectedYear, amount: '', note: '' });
                    setIsPlanCashModalOpen(true);
                  }}
                  className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 text-white rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-xs"
                >
                  <Plus size={13} /> Plan / Record Month Cash
                </button>
                <button
                  onClick={handleSyncLedgerCash}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-xs"
                >
                  <RefreshCcw size={12} className={syncFeedback ? 'animate-spin' : ''} /> Sync Current Cash ({selectedYear})
                </button>
              </div>
            </div>

            {displayedCashHistory.length === 0 ? (
              <div className="p-8 text-center text-slate-400 border border-dashed rounded-2xl">
                No historical cash records found for {selectedYear}. Click "Sync Current Cash" or "Plan / Record Month Cash" to record cash for {selectedYear}.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-slate-800 text-[10px] font-black uppercase tracking-wider text-slate-400">
                      <th className="py-3 px-3">Timestamp Registered</th>
                      <th className="py-3 px-3">Target Month</th>
                      <th className="py-3 px-3">Recorded Cash Amount</th>
                      <th className="py-3 px-3">Balance Formula Role</th>
                      <th className="py-3 px-3">Audit Details</th>
                      <th className="py-3 px-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 font-medium">
                    {displayedCashHistory.map((entry) => {
                      // Check if this entry is the LAST historical amount for its month
                      const isLastForMonth = cashLedgerHistory.find(e => e.monthKey === entry.monthKey)?.id === entry.id;
                      const isEditing = editingCashEntryId === entry.id;

                      return (
                        <tr key={entry.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                          <td className="py-3 px-3 text-slate-500 dark:text-slate-400 font-mono text-[11px]">
                            {entry.timestampFormatted || entry.timestamp}
                          </td>
                          <td className="py-3 px-3 font-bold text-slate-900 dark:text-white">
                            {entry.monthName}
                          </td>
                          <td className="py-3 px-3">
                            {isEditing ? (
                              <div className="flex items-center gap-1.5">
                                <span className="text-slate-400 font-bold">{currency}</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={editCashAmount}
                                  onChange={e => setEditCashAmount(e.target.value)}
                                  className={`w-28 p-1.5 text-xs font-bold rounded-lg border outline-none ${
                                    isDarkMode ? 'bg-[#0D1625] border-blue-500 text-white' : 'bg-white border-blue-500 text-slate-900'
                                  }`}
                                  autoFocus
                                />
                              </div>
                            ) : (
                              <span className="font-['Sora',sans-serif] font-bold text-emerald-600 dark:text-emerald-400 text-sm">
                                {currency} {convertToDisplay(entry.amount).toLocaleString()}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-3">
                            {isLastForMonth ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300">
                                <CheckCircle size={10} /> Considered for Month Balance
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-medium text-slate-400 bg-slate-100 dark:bg-slate-800">
                                Prior Replaced Revision
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-3 text-slate-400 text-[11px]">
                            {isEditing ? (
                              <input
                                type="text"
                                placeholder="Audit note / reason"
                                value={editCashNote}
                                onChange={e => setEditCashNote(e.target.value)}
                                className={`w-full p-1.5 text-xs rounded-lg border outline-none ${
                                  isDarkMode ? 'bg-[#0D1625] border-slate-700 text-white' : 'bg-white border-slate-200 text-slate-900'
                                }`}
                              />
                            ) : (
                              entry.note || entry.source
                            )}
                          </td>
                          <td className="py-3 px-3 text-right">
                            {isEditing ? (
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => handleSaveCashEntry(entry.id)}
                                  className="p-1.5 text-emerald-600 hover:text-emerald-700 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-950/40 transition-colors cursor-pointer"
                                  title="Save changes"
                                >
                                  <Save size={13} />
                                </button>
                                <button
                                  onClick={handleCancelEditCashEntry}
                                  className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                  title="Cancel edit"
                                >
                                  <X size={13} />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => handleStartEditCashEntry(entry)}
                                  className="p-1.5 text-slate-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-950/40 transition-colors cursor-pointer"
                                  title="Edit historical cash amount"
                                >
                                  <Edit2 size={13} />
                                </button>
                                <button
                                  onClick={() => deleteCashLedgerEntry(entry.id)}
                                  className="p-1.5 text-slate-300 hover:text-rose-500 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors cursor-pointer"
                                  title="Delete record"
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Tab 2: Credit Card Payment History Ledger */}
        {activeLedgerTab === 'creditCardLedger' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 text-xs text-slate-400 font-medium pb-2">
              <span>
                Historical register of monthly credit card statement settlements. When you update a card for the next cycle, previous settlement history is preserved here.
              </span>
              <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                Total Settled ({selectedYear}): {currency} {convertToDisplay(displayedCreditCardLedger.reduce((sum, r) => sum + r.amountPaid, 0)).toLocaleString()}
              </span>
            </div>

            {displayedCreditCardLedger.length === 0 ? (
              <div className="p-8 text-center text-slate-400 border border-dashed rounded-2xl">
                No settled credit card payments recorded for {selectedYear}. Click "Mark as Paid" on any active credit card to record settlement in the ledger.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-slate-800 text-[10px] font-black uppercase tracking-wider text-slate-400">
                      <th className="py-3 px-3">Payment Timestamp</th>
                      <th className="py-3 px-3">Card & Issuer</th>
                      <th className="py-3 px-3">Deadline Month</th>
                      <th className="py-3 px-3">Amount Settled</th>
                      <th className="py-3 px-3">Settlement Status</th>
                      <th className="py-3 px-3">Notes</th>
                      <th className="py-3 px-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 font-medium">
                    {displayedCreditCardLedger.map((record) => (
                      <tr key={record.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                        <td className="py-3 px-3 text-slate-500 dark:text-slate-400 font-mono text-[11px]">
                          {record.timestampFormatted || record.paidAt}
                        </td>
                        <td className="py-3 px-3 font-bold text-slate-900 dark:text-white">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 bg-slate-900 text-white text-[8px] font-black uppercase rounded">
                              {record.issuer || 'Card'}
                            </span>
                            <span>{record.cardName}</span>
                            {record.last4 && <span className="text-slate-400 font-mono text-[10px]">•••• {record.last4}</span>}
                          </div>
                        </td>
                        <td className="py-3 px-3 text-slate-700 dark:text-slate-300 font-mono">
                          {record.deadline} ({record.monthKey})
                        </td>
                        <td className="py-3 px-3 font-['Sora',sans-serif] font-bold text-rose-600 dark:text-rose-400 text-sm">
                          −{currency} {convertToDisplay(record.amountPaid).toLocaleString()}
                        </td>
                        <td className="py-3 px-3">
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-teal-100 dark:bg-teal-950/60 text-teal-700 dark:text-teal-300">
                            <Check size={10} /> Paid & Archived
                          </span>
                        </td>
                        <td className="py-3 px-3 text-slate-400 text-[11px]">
                          {record.note || 'Settled'}
                        </td>
                        <td className="py-3 px-3 text-right">
                          <button
                            onClick={() => deleteCreditCardPaymentRecord(record.id)}
                            className="p-1.5 text-slate-300 hover:text-rose-500 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors cursor-pointer"
                            title="Delete settlement record"
                          >
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Jan Baseline Modal */}
      {isBaselineModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-4 animate-in fade-in">
           <div className="bg-white rounded-[40px] max-w-xl w-full p-10 md:p-14 shadow-2xl relative border border-slate-50">
              <button onClick={() => setIsBaselineModalOpen(false)} className="absolute top-8 right-8 text-slate-300 hover:text-slate-900 transition-colors"><X size={24}/></button>
              <div className="mb-8">
                <h2 className="text-3xl font-black text-slate-900 tracking-tighter mb-2">Configure Jan Baseline</h2>
                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest">Set January metrics to compare performance changes</p>
              </div>
              <div className="space-y-6">
                <div className="bg-slate-50/50 p-6 rounded-[32px] border border-slate-100 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 block ml-1">Jan Net Worth ({currency})</label>
                      <input type="number" step="0.01" value={baselineInput.netWorth} onChange={e=>setBaselineInput({...baselineInput, netWorth: e.target.value})} className="w-full p-4 bg-white border border-slate-100 rounded-2xl text-xs font-black outline-none ring-2 ring-transparent focus:ring-slate-900 transition-all" />
                    </div>
                    <div>
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 block ml-1">Jan Total Assets ({currency})</label>
                      <input type="number" step="0.01" value={baselineInput.assets} onChange={e=>setBaselineInput({...baselineInput, assets: e.target.value})} className="w-full p-4 bg-white border border-slate-100 rounded-2xl text-xs font-black outline-none ring-2 ring-transparent focus:ring-blue-600 transition-all" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 block ml-1">Jan Total Liabilities ({currency})</label>
                      <input type="number" step="0.01" value={baselineInput.liabilities} onChange={e=>setBaselineInput({...baselineInput, liabilities: e.target.value})} className="w-full p-4 bg-white border border-slate-100 rounded-2xl text-xs font-black outline-none ring-2 ring-transparent focus:ring-rose-500 transition-all" />
                    </div>
                    <div>
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 block ml-1">Jan Avg Inflow ({currency})</label>
                      <input type="number" step="0.01" value={baselineInput.avgInflow} onChange={e=>setBaselineInput({...baselineInput, avgInflow: e.target.value})} className="w-full p-4 bg-white border border-slate-100 rounded-2xl text-xs font-black outline-none ring-2 ring-transparent focus:ring-teal-600 transition-all" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 block ml-1">Jan Avg Outflow ({currency})</label>
                    <input type="number" step="0.01" value={baselineInput.avgOutflow} onChange={e=>setBaselineInput({...baselineInput, avgOutflow: e.target.value})} className="w-full p-4 bg-white border border-slate-100 rounded-2xl text-xs font-black outline-none ring-2 ring-transparent focus:ring-amber-500 transition-all" />
                  </div>
                </div>
                <div className="flex flex-col gap-3 pt-4">
                  <button onClick={handleSaveBaseline} className="w-full bg-slate-900 text-white font-black py-5 rounded-3xl shadow-xl shadow-slate-200 uppercase tracking-widest text-xs flex items-center justify-center gap-2 hover:bg-blue-600 transition-all">
                    <Save size={16}/> Save January Baseline
                  </button>
                  <button onClick={() => setBaselineInput({ 
                    netWorth: convertToDisplay(currentSnapshot.netWorth).toFixed(2), 
                    assets: convertToDisplay(currentSnapshot.totalAssets).toFixed(2), 
                    liabilities: convertToDisplay(currentSnapshot.liabilities).toFixed(2),
                    avgInflow: convertToDisplay(currentSnapshot.avgInflow).toFixed(2),
                    avgOutflow: convertToDisplay(currentSnapshot.avgOutflow).toFixed(2)
                  })} className="w-full bg-slate-50 text-slate-400 font-black py-3 rounded-2xl uppercase tracking-widest text-[9px] flex items-center justify-center gap-2 hover:bg-slate-100 hover:text-slate-900 transition-all">
                    <RefreshCcw size={12}/> Snap Current Values to Form
                  </button>
                </div>
              </div>
           </div>
        </div>
      )}

      {/* ══ CREDIT CARD ADD / EDIT MODAL ══ */}
      {isCcModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto animate-in fade-in duration-200">
          <div className={`w-full max-w-lg rounded-[36px] border shadow-2xl p-6 sm:p-8 space-y-6 relative my-8 ${isDarkMode ? 'bg-[#111E33] border-[#1A2940] text-white' : 'bg-white border-slate-100 text-slate-900'}`}>
            <div className="flex justify-between items-start pb-4 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-2xl">
                  <CreditCard size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-lg leading-tight">
                    {editingCard ? 'Edit Credit Card' : 'Add Credit Card'}
                  </h3>
                  <p className="text-xs text-slate-400 font-medium mt-0.5">
                    Track statement debt and payment deadlines for current liabilities
                  </p>
                </div>
              </div>
              <button 
                onClick={() => { setIsCcModalOpen(false); setEditingCard(null); }}
                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCcSubmit} className="space-y-4">
              {/* Card Name */}
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5 ml-1">
                  Card Name *
                </label>
                <input
                  required
                  type="text"
                  placeholder="e.g. American Express Gold, Chase Sapphire"
                  value={ccForm.name}
                  onChange={e => setCcForm({ ...ccForm, name: e.target.value })}
                  className={`w-full p-4 rounded-2xl text-xs font-bold outline-none border transition-all ${
                    isDarkMode ? 'bg-[#0D1625] border-[#1A2940] focus:border-blue-500' : 'bg-slate-50 border-slate-200 focus:border-blue-500'
                  }`}
                />
              </div>

              {/* Issuer Quick Chips */}
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5 ml-1">
                  Card Issuer
                </label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {['American Express', 'Chase', 'Apple Card', 'Citi', 'Capital One', 'Other'].map(iss => (
                    <button
                      type="button"
                      key={iss}
                      onClick={() => setCcForm({ ...ccForm, issuer: iss })}
                      className={`px-3 py-1 rounded-xl text-[10px] font-bold transition-all cursor-pointer ${
                        ccForm.issuer === iss
                          ? 'bg-slate-900 text-white shadow-xs'
                          : isDarkMode ? 'bg-[#0D1625] text-slate-400 hover:text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {iss}
                    </button>
                  ))}
                </div>
              </div>

              {/* Last 4 Digits & Amount to Pay */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5 ml-1">
                    Last 4 Digits
                  </label>
                  <input
                    type="text"
                    maxLength={4}
                    placeholder="4821"
                    value={ccForm.last4}
                    onChange={e => setCcForm({ ...ccForm, last4: e.target.value.replace(/\D/g, '') })}
                    className={`w-full p-4 rounded-2xl text-xs font-mono font-bold outline-none border transition-all ${
                      isDarkMode ? 'bg-[#0D1625] border-[#1A2940] focus:border-blue-500' : 'bg-slate-50 border-slate-200 focus:border-blue-500'
                    }`}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5 ml-1">
                    Amount to Pay ({currency}) *
                  </label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="1250.00"
                    value={ccForm.amountToPay}
                    onChange={e => setCcForm({ ...ccForm, amountToPay: e.target.value })}
                    className={`w-full p-4 rounded-2xl text-xs font-bold outline-none border transition-all ${
                      isDarkMode ? 'bg-[#0D1625] border-[#1A2940] focus:border-blue-500' : 'bg-slate-50 border-slate-200 focus:border-blue-500'
                    }`}
                  />
                </div>
              </div>

              {/* Deadline (Due Date) & Minimum Payment */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5 ml-1">
                    Payment Deadline *
                  </label>
                  <input
                    required
                    type="date"
                    value={ccForm.deadline}
                    onChange={e => setCcForm({ ...ccForm, deadline: e.target.value })}
                    className={`w-full p-4 rounded-2xl text-xs font-bold outline-none border transition-all ${
                      isDarkMode ? 'bg-[#0D1625] border-[#1A2940] focus:border-blue-500' : 'bg-slate-50 border-slate-200 focus:border-blue-500'
                    }`}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5 ml-1">
                    Minimum Due ({currency})
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="50.00"
                    value={ccForm.minimumPayment}
                    onChange={e => setCcForm({ ...ccForm, minimumPayment: e.target.value })}
                    className={`w-full p-4 rounded-2xl text-xs font-bold outline-none border transition-all ${
                      isDarkMode ? 'bg-[#0D1625] border-[#1A2940] focus:border-blue-500' : 'bg-slate-50 border-slate-200 focus:border-blue-500'
                    }`}
                  />
                </div>
              </div>

              {/* Credit Limit & APR */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5 ml-1">
                    Credit Limit ({currency})
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="10000"
                    value={ccForm.creditLimit}
                    onChange={e => setCcForm({ ...ccForm, creditLimit: e.target.value })}
                    className={`w-full p-4 rounded-2xl text-xs font-bold outline-none border transition-all ${
                      isDarkMode ? 'bg-[#0D1625] border-[#1A2940] focus:border-blue-500' : 'bg-slate-50 border-slate-200 focus:border-blue-500'
                    }`}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5 ml-1">
                    APR (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="21.99"
                    value={ccForm.apr}
                    onChange={e => setCcForm({ ...ccForm, apr: e.target.value })}
                    className={`w-full p-4 rounded-2xl text-xs font-bold outline-none border transition-all ${
                      isDarkMode ? 'bg-[#0D1625] border-[#1A2940] focus:border-blue-500' : 'bg-slate-50 border-slate-200 focus:border-blue-500'
                    }`}
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5 ml-1">
                  Notes (Optional)
                </label>
                <input
                  type="text"
                  placeholder="Primary dining card, auto-pay configured, etc."
                  value={ccForm.notes}
                  onChange={e => setCcForm({ ...ccForm, notes: e.target.value })}
                  className={`w-full p-4 rounded-2xl text-xs font-bold outline-none border transition-all ${
                    isDarkMode ? 'bg-[#0D1625] border-[#1A2940] focus:border-blue-500' : 'bg-slate-50 border-slate-200 focus:border-blue-500'
                  }`}
                />
              </div>

              <div className="flex gap-3 pt-4">
                {editingCard && (
                  <button
                    type="button"
                    onClick={() => {
                      const idToDelete = editingCard.id;
                      setIsCcModalOpen(false);
                      setEditingCard(null);
                      deleteCreditCard(idToDelete);
                    }}
                    className="px-4 py-4 bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 font-black rounded-2xl uppercase tracking-widest text-xs hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                    title="Delete this card"
                  >
                    <Trash2 size={15} /> Delete
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setIsCcModalOpen(false); setEditingCard(null); }}
                  className="flex-1 py-4 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black rounded-2xl uppercase tracking-widest text-xs hover:bg-slate-200 dark:hover:bg-slate-700 transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-4 bg-slate-900 text-white font-black rounded-2xl uppercase tracking-widest text-xs shadow-xl shadow-slate-200 dark:shadow-none hover:bg-blue-600 transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Save size={16} /> {editingCard ? 'Save Changes' : 'Add Card'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Plan / Record Cash for Specific Month in Year Modal */}
      {isPlanCashModalOpen && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className={`w-full max-w-md rounded-[32px] border p-8 space-y-6 shadow-2xl animate-in zoom-in-95 duration-200 ${
            isDarkMode ? 'bg-[#111E33] border-[#1A2940]' : 'bg-white border-slate-100'
          }`}>
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-xl font-black text-slate-900 dark:text-white font-['Sora',sans-serif]">
                  Record / Plan Cash
                </h3>
                <p className="text-xs text-slate-400 font-medium mt-1">
                  Set registered cash for any specific month to drive the balance formula.
                </p>
              </div>
              <button 
                onClick={() => setIsPlanCashModalOpen(false)} 
                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handlePlanCashSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5 ml-1">
                    Target Month
                  </label>
                  <select
                    value={planCashForm.monthIndex}
                    onChange={e => setPlanCashForm({ ...planCashForm, monthIndex: parseInt(e.target.value, 10) })}
                    className={`w-full p-4 rounded-2xl text-xs font-bold outline-none border transition-all ${
                      isDarkMode ? 'bg-[#0D1625] border-[#1A2940] text-white focus:border-blue-500' : 'bg-slate-50 border-slate-200 text-slate-900 focus:border-blue-500'
                    }`}
                  >
                    {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, idx) => (
                      <option key={m} value={idx}>{m}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5 ml-1">
                    Year
                  </label>
                  <select
                    value={planCashForm.year}
                    onChange={e => setPlanCashForm({ ...planCashForm, year: parseInt(e.target.value, 10) })}
                    className={`w-full p-4 rounded-2xl text-xs font-bold outline-none border transition-all ${
                      isDarkMode ? 'bg-[#0D1625] border-[#1A2940] text-white focus:border-blue-500' : 'bg-slate-50 border-slate-200 text-slate-900 focus:border-blue-500'
                    }`}
                  >
                    {availableYears.map(yr => (
                      <option key={yr} value={yr}>{yr}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5 ml-1">
                  Cash Amount ({currency}) *
                </label>
                <input
                  required
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="35000"
                  value={planCashForm.amount}
                  onChange={e => setPlanCashForm({ ...planCashForm, amount: e.target.value })}
                  className={`w-full p-4 rounded-2xl text-xs font-bold outline-none border transition-all ${
                    isDarkMode ? 'bg-[#0D1625] border-[#1A2940] text-white focus:border-blue-500' : 'bg-slate-50 border-slate-200 text-slate-900 focus:border-blue-500'
                  }`}
                  autoFocus
                />
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5 ml-1">
                  Audit Reason / Note (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g., Year-end bonus deposit, Q1 budget adjustment"
                  value={planCashForm.note}
                  onChange={e => setPlanCashForm({ ...planCashForm, note: e.target.value })}
                  className={`w-full p-4 rounded-2xl text-xs font-bold outline-none border transition-all ${
                    isDarkMode ? 'bg-[#0D1625] border-[#1A2940] text-white focus:border-blue-500' : 'bg-slate-50 border-slate-200 text-slate-900 focus:border-blue-500'
                  }`}
                />
              </div>

              <div className="flex gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setIsPlanCashModalOpen(false)}
                  className="flex-1 py-4 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black rounded-2xl uppercase tracking-widest text-xs hover:bg-slate-200 dark:hover:bg-slate-700 transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-4 bg-slate-900 text-white font-black rounded-2xl uppercase tracking-widest text-xs shadow-xl shadow-slate-200 dark:shadow-none hover:bg-blue-600 transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Save size={16} /> Save Record
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══ DELETE CREDIT CARD CONFIRMATION MODAL ══ */}
      {cardToDelete && (
        <div className="fixed inset-0 z-[120] bg-slate-950/60 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-150">
          <div className={`w-full max-w-md rounded-[32px] border p-6 sm:p-8 space-y-6 shadow-2xl animate-in zoom-in-95 duration-150 ${
            isDarkMode ? 'bg-[#111E33] border-[#1A2940] text-white' : 'bg-white border-slate-100 text-slate-900'
          }`}>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-rose-500/10 text-rose-500 rounded-2xl">
                <Trash2 size={24} />
              </div>
              <div>
                <h3 className="text-lg font-black tracking-tight font-['Sora',sans-serif]">Delete Credit Card</h3>
                <p className="text-xs text-slate-400 font-medium mt-0.5">
                  Confirm liability removal
                </p>
              </div>
            </div>

            <div className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-[#0D1625] border-slate-800' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex justify-between items-center">
                <span className="font-bold text-sm text-slate-800 dark:text-slate-100">{cardToDelete.name}</span>
                {cardToDelete.last4 && (
                  <span className="text-[11px] font-mono text-slate-400">•••• {cardToDelete.last4}</span>
                )}
              </div>
              <div className="mt-2 flex justify-between items-center text-xs">
                <span className="text-slate-400">Amount to pay:</span>
                <span className="font-black text-rose-600 dark:text-rose-400">
                  {currency} {convertToDisplay(cardToDelete.amountToPay).toLocaleString()}
                </span>
              </div>
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              Are you sure you want to delete this credit card? This will remove its liability from your balance sheet and database.
            </p>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setCardToDelete(null)}
                className="flex-1 py-3.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black rounded-2xl uppercase tracking-widest text-xs hover:bg-slate-200 dark:hover:bg-slate-700 transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  deleteCreditCard(cardToDelete.id);
                  setCardToDelete(null);
                }}
                className="flex-1 py-3.5 bg-rose-600 hover:bg-rose-700 text-white font-black rounded-2xl uppercase tracking-widest text-xs shadow-lg shadow-rose-600/20 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <Trash2 size={15} /> Delete Card
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BalanceSheet;